import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type { MarketplaceStatusBadgeVariant } from "./fulfillment-badge-variant";
import type {
  OrderLineVideoReviewStatus,
  OrderScriptRow,
} from "../types/marketplace.types";

/** Line statuses that stay one chip even when videos are attached. */
const LINE_LEVEL_FULFILLMENT_STATUSES = new Set<MarketplaceFulfillmentStatus>([
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

export const VIDEO_REVIEW_STATUSES = [
  "IN_REVIEW",
  "REVISION_REQUESTED",
] as const satisfies readonly OrderLineVideoReviewStatus[];

type VideoStatusSource = Pick<
  OrderScriptRow,
  "fulfillmentStatus" | "videos" | "videoStatusCounts"
>;

export function usesVideoStatusChips(script: VideoStatusSource): boolean {
  if (LINE_LEVEL_FULFILLMENT_STATUSES.has(script.fulfillmentStatus)) {
    return false;
  }
  return videoStatusChips(script).length > 0;
}

export function videoStatusChips(
  script: Pick<OrderScriptRow, "videos" | "videoStatusCounts">,
): { status: OrderLineVideoReviewStatus; count: number }[] {
  const fromCounts = VIDEO_REVIEW_STATUSES.flatMap((status) => {
    const count = script.videoStatusCounts?.[status] ?? 0;
    return count > 0 ? [{ status, count }] : [];
  });
  if (fromCounts.length > 0) return fromCounts;

  const derived: Record<OrderLineVideoReviewStatus, number> = {
    IN_REVIEW: 0,
    REVISION_REQUESTED: 0,
  };
  for (const video of script.videos ?? []) {
    if (video.reviewStatus === "REVISION_REQUESTED") {
      derived.REVISION_REQUESTED += 1;
    } else {
      derived.IN_REVIEW += 1;
    }
  }
  return VIDEO_REVIEW_STATUSES.flatMap((status) =>
    derived[status] > 0 ? [{ status, count: derived[status] }] : [],
  );
}

/** Hue from the existing fulfillment badge tokens. Approved arrives in a later deliverable. */
export function videoReviewBadgeVariant(
  status: OrderLineVideoReviewStatus,
): MarketplaceStatusBadgeVariant {
  if (status === "REVISION_REQUESTED") return "status-error";
  return "status-sent";
}
